import type { Law } from '~/utils/solr/law'
import { createCountWithFilters } from '~/utils/filter-count'
import { extractLabel, getLastName } from '#shared/utils/solr-facet'
import { isHiddenLawId } from '~/utils/law-visibility'
import type { FilteredLaw } from '~/utils/laws/catalogue-sort'
import { buildLawCatalogueGroups } from '~/utils/laws/catalogue-sort'
import { stableSort } from '~/utils/catalogue-relevance'
export interface LawFilterState {
  searchKeyword: string
  showOnlyFavorites: boolean
  hasLawComment: boolean
  selectedLegalArea: { value: string; label: string }[]
  selectedAuthor: { value: string; label: string }[]
  sortFilter: { value: string; label: string }
}

type LawDrawerOverrides = Omit<LawFilterState, 'searchKeyword' | 'sortFilter'>

export function lawMatches(law: FilteredLaw, state: LawFilterState): boolean {
  const title = law.shortTitle || law.title || ''
  const abbreviation = law.abbreviation || ''
  const legalAreas = (law.legal_areas || []).map((area) => extractLabel(area))

  if (!matchesAllSearchTerms(state.searchKeyword, [title, abbreviation, ...legalAreas])) {
    return false
  }

  if (state.hasLawComment && !law.has_commentary) {
    return false
  }

  if (state.showOnlyFavorites && !law.isFavorite) {
    return false
  }

  if (state.selectedLegalArea.length > 0) {
    const lawNames = new Set((law.legal_areas || []).map((area) => extractLabel(area)))
    if (!state.selectedLegalArea.some((area) => lawNames.has(extractLabel(area.value)))) {
      return false
    }
  }

  if (state.selectedAuthor.length > 0) {
    const lawAuthors = new Set(
      (law.authors || []).map((author) => extractName(author).toLowerCase()),
    )
    if (
      !state.selectedAuthor.some((author) =>
        lawAuthors.has(extractName(author.value).toLowerCase()),
      )
    ) {
      return false
    }
  }

  return true
}

export function buildAuthorOptions(
  docs: { authors?: string[] | null }[],
): { value: string; label: string }[] {
  const byName = new Map<string, string>()

  for (const doc of docs) {
    for (const author of doc.authors || []) {
      const key = extractName(author).toLowerCase()
      if (!byName.has(key)) {
        byName.set(key, author)
      }
    }
  }

  return Array.from(byName.values())
    .map((author) => ({ value: author, label: extractName(author) }))
    .sort((a, b) =>
      compareLocaleNorwegian(
        getLastName(a.label).toLowerCase(),
        getLastName(b.label).toLowerCase(),
      ),
    )
}

export const LAW_FACET_CONFIG = {
  selectedLegalArea: {
    docKeys: (law: Law) => (law.legal_areas || []).map((area) => extractLabel(area)),
    optionKey: (value) => extractLabel(value),
  },
  selectedAuthor: {
    docKeys: (law: Law) => (law.authors || []).map((author) => extractName(author).toLowerCase()),
    optionKey: (value) => extractName(value).toLowerCase(),
  },
} as const satisfies Partial<Record<FacetKeys<LawFilterState>, FacetIndexConfig<Law>>>

function useLawFilterState() {
  const searchKeyword = useState<string>('law-filter-search', () => '')
  const showOnlyFavorites = useState<boolean>('law-filter-favorites', () => false)
  const hasLawComment = useState<boolean>('law-filter-commentary', () => false)
  const sortFilter = useState<{ value: string; label: string }>(
    'law-filter-sort',
    () => LAW_SORTING_OPTIONS[0],
  )
  const selectedAuthor = useState<{ value: string; label: string }[]>('law-filter-author', () => [])
  const selectedLegalArea = useState<{ value: string; label: string }[]>(
    'law-filter-legal-area',
    () => [],
  )
  const searchLegalArea = ref<string>('')
  const searchAuthor = ref<string>('')

  return {
    searchKeyword,
    showOnlyFavorites,
    hasLawComment,
    sortFilter,
    selectedAuthor,
    selectedLegalArea,
    searchLegalArea,
    searchAuthor,
  }
}

function useLawSource() {
  const { data: solrData, isLoading, suspense } = useLawQuery()
  const { isFavorite } = useFavorites()
  const totalLaws = computed(() => solrData.value?.response.docs.length ?? 0)
  const sortedSolrLaws = computed<FilteredLaw[]>(() => {
    const docs = (solrData.value?.response.docs ?? []).filter((doc) => !isHiddenLawId(doc.id))
    return stableSort(docs, (a, b) => {
      const titleA = a.shortTitle || a.title || ''
      const titleB = b.shortTitle || b.title || ''
      return compareLocaleNorwegian(titleA, titleB)
    }).map((doc) => ({ ...doc, isFavorite: isFavorite('law', doc.id) }))
  })

  return { isLoading, suspense, totalLaws, sortedSolrLaws }
}

function buildLegalAreaOptions(docs: FilteredLaw[]): { value: string; label: string }[] {
  const allLegalAreas = new Set<string>()
  for (const doc of docs) {
    for (const area of doc.legal_areas) {
      allLegalAreas.add(area)
    }
  }
  return Array.from(allLegalAreas)
    .map((area) => ({ value: area, label: extractLabel(area) }))
    .sort((a, b) => compareLocaleNorwegian(a.label, b.label))
}

function groupLaws(laws: FilteredLaw[]): Record<string, FilteredLaw[]> {
  return laws.reduce(
    (groups, law) => {
      const title = law.shortTitle || law.title || ''
      const firstLetter = title.charAt(0).toUpperCase()
      if (!groups[firstLetter]) {
        groups[firstLetter] = []
      }
      groups[firstLetter].push(law)
      return groups
    },
    {} as Record<string, FilteredLaw[]>,
  )
}

function filterLawGroups(
  grouped: Record<string, FilteredLaw[]>,
  state: LawFilterState,
): Record<string, FilteredLaw[]> {
  const filtered: Record<string, FilteredLaw[]> = {}
  for (const [key, laws] of Object.entries(grouped)) {
    const matches = laws.filter((law) => lawMatches(law, state))
    if (matches.length) {
      filtered[key] = matches
    }
  }
  return filtered
}

function useLawResults(
  source: ReturnType<typeof useLawSource>,
  state: ReturnType<typeof useLawFilterState>,
) {
  const hasFilters = computed(() =>
    Boolean(
      state.searchKeyword.value.length ||
      state.showOnlyFavorites.value ||
      state.hasLawComment.value ||
      state.selectedLegalArea.value.length ||
      state.selectedAuthor.value.length,
    ),
  )
  const legalAreaOptions = computed(() => buildLegalAreaOptions(source.sortedSolrLaws.value))
  const authorOptions = computed(() => buildAuthorOptions(source.sortedSolrLaws.value))
  const committedState = computed<LawFilterState>(() => ({
    searchKeyword: state.searchKeyword.value,
    showOnlyFavorites: state.showOnlyFavorites.value,
    hasLawComment: state.hasLawComment.value,
    selectedLegalArea: state.selectedLegalArea.value,
    selectedAuthor: state.selectedAuthor.value,
    sortFilter: state.sortFilter.value,
  }))
  const groupedLaws = computed(() => groupLaws(source.sortedSolrLaws.value))
  const filteredLaws = computed(() =>
    hasFilters.value ? filterLawGroups(groupedLaws.value, committedState.value) : groupedLaws.value,
  )
  const lawGroups = computed(() =>
    buildLawCatalogueGroups(
      filteredLaws.value,
      state.searchKeyword.value,
      state.sortFilter.value.value,
    ),
  )
  const totalFilteredLaws = computed(() =>
    Object.values(filteredLaws.value).reduce((total, laws) => total + laws.length, 0),
  )

  return {
    hasFilters,
    legalAreaOptions,
    authorOptions,
    committedState,
    lawGroups,
    totalFilteredLaws,
  }
}

function useLawFacets(
  source: ReturnType<typeof useLawSource>,
  state: ReturnType<typeof useLawFilterState>,
  results: ReturnType<typeof useLawResults>,
) {
  const countForFacet = createCountWithFilters(
    source.sortedSolrLaws,
    results.committedState,
    lawMatches,
  )
  const facetCounts = useFacetIndex(
    source.sortedSolrLaws,
    results.committedState,
    lawMatches,
    LAW_FACET_CONFIG,
  )
  const {
    selected: legalAreaSelected,
    toggle: toggleLegalArea,
    facetOptions: legalAreaFacetOptions,
  } = useFacetFilter(
    state.selectedLegalArea,
    results.legalAreaOptions,
    facetCounts.selectedLegalArea,
  )
  const {
    selected: authorSelected,
    toggle: toggleAuthor,
    facetOptions: authorFacetOptions,
  } = useFacetFilter(state.selectedAuthor, results.authorOptions, facetCounts.selectedAuthor)
  const countWithFilters = (overrides: Partial<LawDrawerOverrides>) => countForFacet(overrides)
  return {
    countWithFilters,
    legalAreaSelected,
    toggleLegalArea,
    legalAreaFacetOptions,
    authorSelected,
    toggleAuthor,
    authorFacetOptions,
    hasLawCommentCount: computed(() => countWithFilters({ hasLawComment: true })),
  }
}

export function _useLawFilters() {
  const state = useLawFilterState()
  const source = useLawSource()
  const results = useLawResults(source, state)
  const facets = useLawFacets(source, state, results)

  return {
    ...state,
    ...facets,
    isLoading: source.isLoading,
    suspense: source.suspense,
    totalLaws: source.totalLaws,
    hasFilters: results.hasFilters,
    totalFilteredLaws: results.totalFilteredLaws,
    legalAreaOptions: results.legalAreaOptions,
    authorOptions: results.authorOptions,
    lawGroups: results.lawGroups,
    toggleFavorite: () => (state.showOnlyFavorites.value = !state.showOnlyFavorites.value),
    toggleHasLawComment: () => (state.hasLawComment.value = !state.hasLawComment.value),
  }
}

export const useLawFilters = createSharedComposable(_useLawFilters)
